/** @format */
import { css } from 'styled-components';

import {
    MediaQuery,
    desktop,
    largeTablet,
    smallTablet,
} from '@toucan/client/global/application';
import { headerSizes } from '@toucan/client/global/layout';
import { theme } from '@toucan/client/global/theme';

import { StyledHeaderProps } from './index';

export const headerPaddingVariations = {
    [MediaQuery.DESKTOP]: {
        normal: `4.8rem 0`,
        sticky: `2.4rem 0`,
    },
    [MediaQuery.LARGE_TABLET]: '2.8rem 0',
    [MediaQuery.SMALL_TABLET]: '2rem 0',
};

/**
 * Base styles for the header component
 */
const baseStyles = ({
    isSmallHeader,
    isSearchBarOpen,
    showAdditionalHeader,
}: StyledHeaderProps) => css`
    background: var(--colors-surface-inverse);
    position: fixed;
    top: 0;
    z-index: ${isSearchBarOpen ? 10000 : 10};
    width: 100%;
    box-shadow: ${isSmallHeader && !showAdditionalHeader
        ? 'var(--effects-shadow-md)'
        : 'none'};

    & + .header-shim {
        display: block;
        height: ${headerSizes[MediaQuery.DESKTOP].full / 10}rem;
    }

    .header-content-wrapper {
        background-color: var(--colors-surface-inverse);
    }

    .header-content {
        display: grid;
        display: -ms-grid;
        grid-template-columns: 2fr 1fr;
        -ms-grid-columns: 2fr 1fr;
        align-items: center;
        background-color: var(--colors-surface-inverse);
        padding: ${headerPaddingVariations[MediaQuery.DESKTOP].normal};
    }

    .shop-actions {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-right: var(--layout-spacing-24);
        height: 4.8rem;

        .menu {
            margin-right: var(--layout-spacing-12);
        }

        .logo-link {
            &:after {
                display: none;
            }
        }

        .logo {
            cursor: pointer;
            margin-right: var(--layout-spacing-16);
            margin-left: var(--layout-spacing-12);
        }

        .search-bar-wrapper {
            position: relative;
            flex: 1;

            .search-bar__typeahead {
                border-radius: 10rem;
                height: 60px;
                display: flex;
                align-items: center;

                .input {
                    width: 100%;
                }

                .typeahead-items,
                .typeahead__no-suggestions-container {
                    padding-top: var(--layout-spacing-48);
                    border-bottom-left-radius: 2.4rem;
                    border-bottom-right-radius: 2.4rem;
                }
            }
        }
    }

    .user-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;

        > button {
            margin-right: 2.4rem;

            &:last-child {
                margin-right: 0;
            }
        }

        .header-account-menu button {
            margin-right: 0;
            display: flex;
        }

        .icon {
            position: relative;
            cursor: pointer;
        }

        .logged-in-icon {
            background-color: var(--colors-surface-primary);
            border-color: var(--colors-text-inverse);
        }

        .user-actions-icons {
            display: flex;
            align-items: center;

            button {
                opacity: 1;
            }

            > * {
                margin-left: var(--layout-spacing-32);
            }
        }
    }

    .sell-and-trade-container {
        display: flex;
        flex-direction: column;
        padding-left: var(--layout-spacing-24);
        position: relative;
        white-space: nowrap;

        &:before {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            transition: 500ms;
            height: 64px;
            content: '';
            border-left: 1px solid var(--colors-border-inverse);
        }

        a {
            font-weight: var(--typography-weight-bold);
        }

        > span {
            ${theme.typography.caption_highlight}
            color: var(--colors-text-accent);
        }
    }
`;

const desktopStyles = () => css`
    .user-actions .user-actions-icons > * {
        margin-left: var(--layout-spacing-24);
    }
`;
/**
 * Large tablet styles for the header component
 *
 */
const largeTabletStyles = ({ isSearchBarOpen }: StyledHeaderProps) => css`
    & + .header-shim {
        height: ${headerSizes[MediaQuery.LARGE_TABLET] / 10}rem;
    }

    .header-content {
        padding: ${headerPaddingVariations[MediaQuery.LARGE_TABLET]};
    }

    .user-actions .user-actions-icons > * {
        margin-left: var(--layout-spacing-24);
    }

    .shop-actions {
        padding-right: var(--layout-spacing-16);
        height: auto;

        .menu {
            margin-right: var(--layout-spacing-16);
        }

        .search-bar-wrapper .search-bar__typeahead {
            .input {
                width: 100%;

                input,
                label {
                    padding: var(--layout-spacing-24);
                }
            }
        }
    }

    .sell-and-trade-container {
        padding-left: var(--layout-spacing-16);
    }
`;

/**
 * Small tablet styles for the header component
 *
 */
const smallTabletStyles = ({ isSearchBarOpen }: StyledHeaderProps) => css`
    & + .header-shim {
        height: ${headerSizes[MediaQuery.SMALL_TABLET] / 10}rem;
    }

    .header-content {
        grid-template-columns: 1fr 6rem;
        padding: ${headerPaddingVariations[MediaQuery.SMALL_TABLET]};
    }

    .shop-actions {
        padding-right: 0;

        .logo {
            /* override the Logo styles to ensure there is no CLS/flash */
            width: 8.5rem;
            height: 4.25rem;
            margin-right: var(--layout-spacing-16);
        }

        ${isSearchBarOpen
            ? `
            .search-bar-wrapper {
                position: unset;
            }
        `
            : ``}
    }

    .user-actions {
        justify-content: flex-end;
    }

    .sell-and-trade-container,
    .account-menu-button {
        display: none;
    }
`;

/**
 * The header component styles
 *
 * @param {StyledHeaderProps} props The header properties object
 * */
export const styles = (props: StyledHeaderProps) => css`
    ${baseStyles(props)}
    ${desktop(desktopStyles())}
    ${largeTablet(largeTabletStyles(props))}
    ${smallTablet(smallTabletStyles(props))}
`;
